import { WaypointType } from '@treadinc/horizon-api-spec';

import { GeofenceTrigger } from '~hooks/useGeofenceTriggers/models';
import { isDefaultSiteUsages, type JobSiteUsage } from '~hooks/useSiteUsages/models';

import { type WaypointMarker } from './GeofenceDebugMap';

type SideBySideDiffRow = {
  historical: GeofenceTrigger | null;
  current: GeofenceTrigger | null;
  sortTime: number;
};

type MovingSiteInfo = {
  siteIndex: number;
  movingSiteId: string;
  radius: number | null;
};

/**
 * Identity used to align historical and current triggers in the side-by-side
 * diff. Two triggers match when their (occurred_at down to the second, geofence
 * name, tag, type) tuple is equal.
 */
export function triggerKey(trigger: GeofenceTrigger): string {
  return [
    trigger.triggeredLocation.occurredAt.startOf('second').toISOString(),
    (trigger.geofence?.geofence?.name ?? '').trim(),
    trigger.geofence?.tag ?? '',
    trigger.triggerType,
  ].join('-');
}

/**
 * Merges historical and current trigger lists into a single sorted list of
 * diff rows. Rows missing one side represent triggers that exist only in the
 * historical or current run.
 */
export function buildSideBySideDiffRows(
  historicalTriggers: GeofenceTrigger[] | null,
  currentTriggers: GeofenceTrigger[],
): SideBySideDiffRow[] {
  const byKey = new Map<
    string,
    { historical: GeofenceTrigger | null; current: GeofenceTrigger | null }
  >();
  for (const t of historicalTriggers ?? []) {
    const k = triggerKey(t);
    const existing = byKey.get(k) ?? { historical: null, current: null };
    existing.historical = t;
    byKey.set(k, existing);
  }
  for (const t of currentTriggers) {
    const k = triggerKey(t);
    const existing = byKey.get(k) ?? { historical: null, current: null };
    existing.current = t;
    byKey.set(k, existing);
  }
  const rows = Array.from(byKey.values()).map((v) => ({
    ...v,
    sortTime:
      v.historical?.triggeredLocation.occurredAt.valueOf() ??
      v.current?.triggeredLocation.occurredAt.valueOf() ??
      0,
  }));
  rows.sort((a, b) => a.sortTime - b.sortTime);
  return rows;
}

/**
 * Locates the first site usage that backs a moving site and returns its index,
 * moving-site id, and the radius of its moving geofence (if any). Returns null
 * when no site usage references a moving site.
 */
export function getMovingSiteInfo(siteUsages: JobSiteUsage[]): MovingSiteInfo | null {
  for (let i = 0; i < siteUsages.length; i++) {
    const su = siteUsages[i];
    const movingSite = su.site.movingSite;
    if (!movingSite?.id) continue;
    const primary = su.site.nextBillionGeofence;
    const movingGeofence =
      (primary?.movingGeofence ? primary : null) ??
      su.additionalGeofences.find((g) => g.movingGeofence);
    return {
      siteIndex: i,
      movingSiteId: movingSite.id,
      radius: movingGeofence?.circleRadius ?? null,
    };
  }
  return null;
}

/**
 * Builds the debug-map waypoint markers from site usages: one per site with
 * valid coordinates, collapsed to `'both'` when a site is used for multiple
 * waypoint types, flagged as a default (candidate) site when every usage is a
 * default, and repositioned to the live moving-site location when applicable.
 */
export function buildWaypointMarkers(
  siteUsages: JobSiteUsage[],
  movingSiteInfo: MovingSiteInfo | null,
  movingSitePosition: { position: { lat: number; lng: number } } | null,
): WaypointMarker[] {
  return siteUsages
    .map((su, i): WaypointMarker | null => {
      if (!su.site.lat || !su.site.lon) return null;
      const types = su.usages.map((u) => u.waypointType);
      const waypointType: WaypointType | 'both' =
        types.length > 1 ? 'both' : (types[0] ?? WaypointType.PICKUP);
      const isDefaultSite = isDefaultSiteUsages(su.usages);
      const onMovingSite = movingSiteInfo?.siteIndex === i && !!movingSitePosition;
      return {
        key: su.site.id,
        position:
          onMovingSite && movingSitePosition
            ? movingSitePosition.position
            : { lat: Number(su.site.lat), lng: Number(su.site.lon) },
        waypointType,
        isDefaultSite,
      };
    })
    .filter((m): m is WaypointMarker => m !== null);
}

/** Number of distinct loads referenced across every site usage entry. */
export function getTotalLoadCount(siteUsages: JobSiteUsage[]): number {
  const ids = new Set<string>();
  for (const su of siteUsages) {
    for (const usage of su.usages) {
      for (const load of usage.loads) {
        ids.add(load.id);
      }
    }
  }
  return ids.size;
}
